import { apiService } from '../../services/api';
import type { AiBudget, HealthReport, UsageSummary } from '../../types/aiOps';
import { DEFAULT_BUDGET } from '../../types/aiOps';

const BASE = '/api/ai-ops';

/** The backend on this server predates `/api/ai-ops` — settings cannot fix that. */
export class AiOpsUnavailableError extends Error {
  constructor(message = 'AI operations are not available on this server yet.') {
    super(message);
    this.name = 'AiOpsUnavailableError';
  }
}

/** One short line an admin can act on, never a raw stack or a bare status code */
export function aiOpsErrorMessage(error: any): string {
  if (error instanceof AiOpsUnavailableError) return error.message;
  const data = error?.response?.data;
  // This API answers either { message } or { error: { message } }
  const body = data?.message ?? data?.error?.message;
  const raw = Array.isArray(body) ? body.join(', ') : body || error?.message || '';
  const text = String(raw).trim();
  const status = error?.response?.status;
  if (status === 401) return 'Your session has expired — sign in again';
  if (status === 403) return 'You do not have permission to do that';
  if (/network error|failed to fetch|load failed/i.test(text)) return 'Cannot reach the server — check your connection';
  if (status >= 500) return text && text.length <= 140 ? `Server error: ${text}` : 'The server could not complete that — try again';
  return text || 'Something went wrong';
}

/** A 404 here means the module is not deployed, not "nothing found" */
function asUnavailable(error: any): never {
  if (error?.response?.status === 404) throw new AiOpsUnavailableError();
  throw error;
}

/* ------------------------------------------------------------------ */
/* Health                                                              */
/* ------------------------------------------------------------------ */

export async function fetchHealth(): Promise<HealthReport> {
  try {
    const res = await apiService.get<HealthReport>(`${BASE}/health`);
    return res.data;
  } catch (error) {
    return asUnavailable(error);
  }
}

/** Runs every capability check now, rather than reading the last stored result */
export async function runHealthCheck(): Promise<HealthReport> {
  try {
    const res = await apiService.post<HealthReport>(`${BASE}/health/check`, {});
    return res.data;
  } catch (error) {
    return asUnavailable(error);
  }
}

/* ------------------------------------------------------------------ */
/* Usage                                                               */
/* ------------------------------------------------------------------ */

export async function fetchUsage(month?: string): Promise<UsageSummary> {
  try {
    const res = await apiService.get<UsageSummary>(`${BASE}/usage`, {
      params: month ? { month } : undefined,
    });
    return res.data;
  } catch (error) {
    return asUnavailable(error);
  }
}

/* ------------------------------------------------------------------ */
/* Budget                                                              */
/* ------------------------------------------------------------------ */

const numberOr = (value: unknown, fallback: number): number =>
  value === null || value === undefined || Number.isNaN(Number(value)) ? fallback : Number(value);

/**
 * The ceiling, whether it arrives bare or wrapped in the budget state the
 * server also reports (`{ budget, spentCents, … }`). A missing number falls
 * back to the default rather than reaching a form field as NaN.
 */
export function toBudget(payload: any): AiBudget {
  const source = payload && typeof payload === 'object' && payload.budget ? payload.budget : payload;
  return {
    monthlyCapCents: numberOr(source?.monthlyCapCents, DEFAULT_BUDGET.monthlyCapCents),
    pauseScheduledAt: numberOr(source?.pauseScheduledAt, DEFAULT_BUDGET.pauseScheduledAt),
    warnAt: numberOr(source?.warnAt, DEFAULT_BUDGET.warnAt),
  };
}

export async function fetchBudget(): Promise<AiBudget> {
  try {
    const res = await apiService.get<AiBudget>(`${BASE}/budget`);
    return toBudget(res.data);
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function saveBudget(input: Partial<AiBudget>): Promise<AiBudget> {
  try {
    const res = await apiService.patch<AiBudget>(`${BASE}/budget`, input);
    return toBudget(res.data);
  } catch (error) {
    return asUnavailable(error);
  }
}
